'use client';

import { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { Treatment } from '@/lib/treatments-data';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Shield, Award, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface HeroSectionProps {
    treatment: Treatment;
}

const beforeAfterImages = [
    {
        before: '/images/treatments/hollywood-smile/cover-before.webp',
        after: '/images/treatments/hollywood-smile/cover-after.webp',
        descriptionKey: 'hero.beforeAfter.descriptions.transformation'
    }
];

export default function HollywoodSmileHeroSection({ treatment }: HeroSectionProps) {
    const { t } = useTranslation('hollywood-smile');
    const { isRTL } = useRTL();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [showAfter, setShowAfter] = useState(false);
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Hero title animation - fade up + slide in
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 60, scale: 0.95 },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 1.2,
                    ease: "power3.out",
                    delay: 0.3
                }
            );

            // Subtitle animation
            gsap.fromTo(subtitleRef.current,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    delay: 0.6,
                    ease: "power3.out"
                }
            );

            // CTA animation
            gsap.fromTo(ctaRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 0.9,
                    ease: "power3.out"
                }
            );

            // Image container animation
            gsap.fromTo(imageRef.current,
                { opacity: 0, scale: 0.8, x: 50 },
                {
                    opacity: 1,
                    scale: 1,
                    x: 0,
                    duration: 1.5,
                    delay: 0.4,
                    ease: "power3.out"
                }
            );

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    // Auto-cycle through images
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    // Auto-toggle before/after
    useEffect(() => {
        const toggleInterval = setInterval(() => {
            setShowAfter(prev => !prev);
        }, 3000);

        return () => clearInterval(toggleInterval);
    }, []);

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % beforeAfterImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + beforeAfterImages.length) % beforeAfterImages.length);
    };

    const handleConsultationClick = () => {
        const consultationSection = document.getElementById('consultation') || document.getElementById('booking') || document.getElementById('final-cta');
        if (consultationSection) {
            consultationSection.scrollIntoView({ behavior: 'smooth' });
        } else {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('[handleConsultationClick] No booking section found. Tried IDs: consultation, booking, final-cta');
            }
            window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
        }
    };

    const handleGalleryClick = () => {
        const gallerySection = document.getElementById('gallery');
        gallerySection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            id="hero"
            ref={sectionRef}
            className="relative min-h-[65vh] bg-gradient-to-br from-navy to-navy-950 overflow-hidden"
        >
            {/* Background decorative elements */}
            <div className="absolute inset-0">
                <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold opacity-10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-gold opacity-10 rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold opacity-5 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative py-10 lg:py-16">
                <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[52vh]">
                    {/* Left Content */}
                    <div className={cn("space-y-8 text-center", isRTL ? "lg:text-right" : "lg:text-left")}>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className={cn(
                                "inline-flex items-center bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-medium",
                                isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                            )}
                        >
                            <Sparkles className="w-4 h-4" />
                            <span>{t('hero.badge')}</span>
                        </motion.div>

                        <h1
                            ref={titleRef}
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight font-serif"
                        >
                            {t('hero.title')}{' '}
                            <span className="text-gold relative">
                                {t('hero.titleHighlight')}
                                <motion.div
                                    className="absolute -inset-2 bg-gold/20 rounded-lg"
                                    animate={{
                                        boxShadow: [
                                            '0 0 20px rgba(212, 175, 55, 0.3)',
                                            '0 0 30px rgba(212, 175, 55, 0.5)',
                                            '0 0 20px rgba(212, 175, 55, 0.3)'
                                        ]
                                    }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                />
                            </span>{' '}
                            {t('hero.location')}
                        </h1>

                        <p
                            ref={subtitleRef}
                            className="text-xl md:text-2xl text-slate-300 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                        >
                            {t('hero.subtitle')}
                        </p>

                        <div ref={ctaRef} className="space-y-6">
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Button
                                        onClick={handleConsultationClick}
                                        size="lg"
                                        className="bg-gold hover:bg-gold-600 text-navy px-8 py-6 text-lg font-semibold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-gold hover:border-gold-600"
                                    >
                                        {t('hero.buttons.bookConsultation')}
                                        {isRTL ? (
                                            <ArrowRight className="w-5 h-5 mr-2 rotate-180" />
                                        ) : (
                                            <ArrowRight className="w-5 h-5 ml-2" />
                                        )}
                                    </Button>
                                </motion.div>

                                <button
                                    onClick={handleGalleryClick}
                                    className="text-gold hover:text-gold-600 transition-colors duration-300 font-medium text-lg border-b-2 border-gold hover:border-gold-600 pb-1"
                                >
                                    {t('hero.buttons.exploreGallery')}
                                </button>
                            </div>

                            {/* Trust indicators */}
                            <div className="flex flex-wrap justify-center lg:justify-start items-center gap-6 text-sm text-slate-300">
                                <motion.div
                                    className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Star className="w-5 h-5 text-gold fill-current" />
                                    <span>{t('hero.trustIndicators.rating')}</span>
                                </motion.div>
                                <motion.div
                                    className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Shield className="w-5 h-5 text-gold" />
                                    <span>{t('hero.trustIndicators.lifetime')}</span>
                                </motion.div>
                                <motion.div
                                    className={cn(
                                        "flex items-center",
                                        isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                    )}
                                    whileHover={{ scale: 1.05 }}
                                >
                                    <Award className="w-5 h-5 text-gold" />
                                    <span>{t('hero.trustIndicators.experience')}</span>
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Right Image - Before/After Slider */}
                    <div className="relative flex justify-center lg:justify-end">
                        <div ref={imageRef} className="relative z-10">
                            <div className="w-80 h-80 md:w-96 md:h-96 relative">
                                {/* Before/After Image Container */}
                                <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl border-4 border-gold/30">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={`${currentImageIndex}-${showAfter}`}
                                            initial={{ opacity: 0, scale: 1.1 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            transition={{ duration: 0.8 }}
                                            className="relative w-full h-full"
                                        >
                                            <OptimizedImage
                                                src={showAfter ? beforeAfterImages[currentImageIndex].after : beforeAfterImages[currentImageIndex].before}
                                                alt={showAfter ? t('hero.beforeAfter.after') : t('hero.beforeAfter.before')}
                                                width={400}
                                                height={400}
                                                className="w-full h-full object-cover"
                                            />

                                            {/* Before/After Label */}
                                            <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                                                {showAfter ? t('hero.beforeAfter.after') : t('hero.beforeAfter.before')}
                                            </div>
                                        </motion.div>
                                    </AnimatePresence>

                                    {/* Navigation arrows — hidden when only one slide */}
                                    {beforeAfterImages.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevImage}
                                                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300"
                                            >
                                                <ChevronLeft className="w-6 h-6" />
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all duration-300"
                                            >
                                                <ChevronRight className="w-6 h-6" />
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Floating elements — smaller + tucked in on
                                    mobile so they don't overflow. */}
                                <motion.div
                                    className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-12 h-12 sm:w-16 sm:h-16 bg-gold rounded-full flex items-center justify-center shadow-lg"
                                    animate={{
                                        y: [0, -10, 0],
                                        rotate: [0, 5, -5, 0]
                                    }}
                                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                                >
                                    <span className="text-xl sm:text-2xl">✨</span>
                                </motion.div>
                                <motion.div
                                    className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-10 h-10 sm:w-12 sm:h-12 bg-white rounded-full flex items-center justify-center shadow-lg"
                                    animate={{
                                        y: [0, 8, 0],
                                        rotate: [0, -5, 5, 0]
                                    }}
                                    transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                                >
                                    <span className="text-lg sm:text-xl">😊</span>
                                </motion.div>

                                {/* Image indicator dots — hidden when only one slide */}
                                {beforeAfterImages.length > 1 && (
                                    <div className={cn(
                                        "absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex",
                                        isRTL ? "space-x-reverse space-x-2" : "space-x-2"
                                    )}>
                                        {beforeAfterImages.map((_, index) => (
                                            <button
                                                key={index}
                                                onClick={() => setCurrentImageIndex(index)}
                                                className={`w-3 h-3 rounded-full transition-all duration-300 ${index === currentImageIndex
                                                        ? 'bg-gold scale-125'
                                                        : 'bg-white/50 hover:bg-white/70'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll indicator */}
                <motion.div
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-slate-300"
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                    <div className="flex flex-col items-center space-y-2">
                        <span className="text-sm">{t('hero.bottomText')}</span>
                        <div className="w-px h-8 bg-gold"></div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
